/**
 * Estado de un formulario del ERP.
 *
 * Lo que hay aquí no es «gestión de estado»: son las cuatro decisiones que
 * distinguen un formulario que se puede usar seis horas al día de uno que
 * hace perder trabajo.
 *
 * 1. **Los errores del servidor se colocan en su campo.** La API devuelve
 *    RFC 9457 con una lista de `{ path, message }`. Enseñarla como un
 *    párrafo rojo arriba obliga a buscar a mano cuál de los treinta campos
 *    falló.
 * 2. **Lo que no se ha tocado no se envía.** Un `PATCH` que reenvía los
 *    treinta campos pisa lo que otra persona cambió mientras tanto,
 *    aunque aquí nadie tocara ese campo.
 * 3. **El conflicto de versión se cuenta como lo que es.** No «error 409»
 *    sino «alguien lo ha cambiado mientras lo editabas», con la opción de
 *    recargar.
 * 4. **Los campos vacíos se distinguen de los no rellenados.** Vaciar un
 *    teléfono es `null`; no tocarlo es no mandarlo.
 */

export type FieldValue = string | number | boolean | null;

export type FormValues = Record<string, FieldValue>;

export type FieldErrors = Record<string, string>;

export interface ValidationRule {
  /** Devuelve el mensaje de error, o `null` si el valor vale. */
  (value: FieldValue, values: FormValues): string | null;
}

export type FieldRules = Record<string, ValidationRule[]>;

// ═══════════════════════════════════════════════════════════════════════
// Reglas
// ═══════════════════════════════════════════════════════════════════════

export const required =
  (label = 'Este campo'): ValidationRule =>
  (value) =>
    value === null || value === '' || value === undefined ? `${label} es obligatorio.` : null;

export const maxLength =
  (max: number): ValidationRule =>
  (value) =>
    typeof value === 'string' && value.length > max
      ? `No puede tener más de ${max} caracteres.`
      : null;

export const minLength =
  (min: number): ValidationRule =>
  (value) =>
    typeof value === 'string' && value !== '' && value.length < min
      ? `Debe tener al menos ${min} caracteres.`
      : null;

export const email = (): ValidationRule => (value) =>
  typeof value === 'string' && value !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
    ? 'El correo no tiene un formato válido.'
    : null;

export const isoDate = (): ValidationRule => (value) =>
  typeof value === 'string' && value !== '' && !/^\d{4}-\d{2}-\d{2}$/.test(value)
    ? 'La fecha debe tener el formato AAAA-MM-DD.'
    : null;

export const nonNegative = (): ValidationRule => (value) =>
  typeof value === 'number' && value < 0 ? 'No puede ser negativo.' : null;

/**
 * Fecha que no puede estar en el futuro.
 *
 * Se usa en fechas de hecho consumado —puesta en servicio, último
 * mantenimiento—: una fecha futura ahí no es un error de dedo, es un dato
 * que después falsea el cálculo de vencimientos.
 */
export const notInFuture =
  (today: string): ValidationRule =>
  (value) =>
    typeof value === 'string' && value !== '' && value > today
      ? 'No puede ser una fecha futura.'
      : null;

// ═══════════════════════════════════════════════════════════════════════
// Validación
// ═══════════════════════════════════════════════════════════════════════

/** Valida un campo con sus reglas, en orden. El primer fallo manda. */
export function validateField(
  field: string,
  values: FormValues,
  rules: FieldRules,
): string | null {
  for (const rule of rules[field] ?? []) {
    const error = rule(values[field] ?? null, values);
    if (error) return error;
  }
  return null;
}

/** Valida el formulario entero. Se llama al enviar, no al teclear. */
export function validateAll(values: FormValues, rules: FieldRules): FieldErrors {
  const errors: FieldErrors = {};
  for (const field of Object.keys(rules)) {
    const error = validateField(field, values, rules);
    if (error) errors[field] = error;
  }
  return errors;
}

// ═══════════════════════════════════════════════════════════════════════
// Lo que se envía
// ═══════════════════════════════════════════════════════════════════════

/**
 * Qué cambió respecto a lo que había.
 *
 * Se comparan valores, no referencias: escribir y borrar deja el campo
 * como estaba y **no** debe viajar. Un `PATCH` con campos que nadie tocó
 * pisa el trabajo de quien esté editando la misma ficha.
 */
export function changedValues(initial: FormValues, current: FormValues): FormValues {
  const changes: FormValues = {};

  for (const [key, value] of Object.entries(current)) {
    const before = initial[key] ?? null;
    const after = value ?? null;

    // Una cadena vacía y un nulo son lo mismo para el usuario —el campo
    // está vacío— pero distintos para JSON. Se normalizan al comparar.
    const same =
      before === after || (isBlank(before) && isBlank(after));

    if (!same) changes[key] = after;
  }

  return changes;
}

function isBlank(value: FieldValue): boolean {
  return value === null || value === '' || value === undefined;
}

/**
 * Prepara el cuerpo de la petición.
 *
 * Las cadenas vacías se convierten en `null` **sólo** si el campo admite
 * nulo: para el resto, la API rechazaría `null` y el mensaje sería peor
 * que no enviar el campo.
 */
export function toRequestBody(values: FormValues, nullable: readonly string[]): FormValues {
  const body: FormValues = {};
  const nullableSet = new Set(nullable);

  for (const [key, value] of Object.entries(values)) {
    if (value === '' ) {
      if (nullableSet.has(key)) body[key] = null;
      continue;
    }
    body[key] = value;
  }

  return body;
}

// ═══════════════════════════════════════════════════════════════════════
// Errores que vienen del servidor
// ═══════════════════════════════════════════════════════════════════════

export interface ServerFieldError {
  path?: string;
  message?: string;
}

/**
 * Coloca los errores del servidor en sus campos.
 *
 * `path` llega de Zod como `fiscalAddress.city` o `lines.0.quantity`. Se
 * usa el camino completo como clave y también el primer tramo, para que un
 * error de un subcampo marque al menos el bloque que lo contiene y no se
 * pierda.
 */
export function mapServerErrors(errors: readonly ServerFieldError[]): FieldErrors {
  const mapped: FieldErrors = {};

  for (const error of errors) {
    const path = error.path?.trim();
    const message = error.message?.trim();
    if (!path || !message) continue;

    mapped[path] ??= message;

    const root = path.split('.')[0];
    if (root && root !== path) mapped[root] ??= message;
  }

  return mapped;
}

/** ¿Queda algún error sin colocar en un campo del formulario? */
export function unplacedErrors(mapped: FieldErrors, knownFields: readonly string[]): string[] {
  const known = new Set(knownFields);
  return Object.entries(mapped)
    .filter(([field]) => !known.has(field))
    .map(([field, message]) => `${field}: ${message}`);
}
